import { useState } from 'react';
import { reviews, establishments, stats } from '../../../mocks/reviews';

export default function ReviewsSection() {
  const [activeEstab, setActiveEstab] = useState('all');
  const [helpfulClicked, setHelpfulClicked] = useState<string[]>([]);

  const filtered = activeEstab === 'all'
    ? reviews
    : reviews.filter((r) => r.establishmentId === activeEstab);

  const handleHelpful = (id: string) => {
    setHelpfulClicked((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  return (
    <section id="reviews" className="py-24 bg-[#FAF8F5]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="inline-block bg-[#FF6B35]/10 text-[#FF6B35] text-xs font-bold px-3 py-1 rounded-full mb-4 tracking-widest uppercase">
            Establishment Reviews
          </span>
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.02em' }}>
            What Georgetown <span className="text-[#FF6B35]">Loves</span>
          </h2>
          <p className="text-gray-500 max-w-lg mx-auto text-sm leading-relaxed">
            Real reviews from real customers across Georgetown, Guyana. Honest feedback to help you choose the best partner stores.
          </p>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {stats.map((stat) => (
            <div key={stat.label} className="bg-white rounded-2xl p-5 text-center">
              <p className="font-black text-2xl text-gray-900">{stat.value}</p>
              <p className="text-gray-400 text-xs mt-1">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Filter by Establishment */}
        <div className="flex flex-wrap gap-2 mb-8">
          {establishments.map((e) => (
            <button
              key={e.id}
              onClick={() => setActiveEstab(e.id)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeEstab === e.id
                  ? 'bg-[#0D0D0D] text-white'
                  : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'
              }`}
            >
              {e.label}
            </button>
          ))}
        </div>

        {/* Reviews Grid */}
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-3xl border border-gray-100">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#FFF5F0] text-[#FF6B35] mb-5">
              <i className="ri-chat-3-line text-3xl" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">No reviews yet</h3>
            <p className="text-sm text-gray-500 max-w-xs text-center leading-relaxed">
              Be the first to share your experience with one of our partner stores in Georgetown!
            </p>
            <button className="mt-6 flex items-center gap-2 bg-[#0D0D0D] text-white font-semibold px-6 py-3 rounded-full text-sm hover:bg-[#1f1f1f] transition-colors cursor-pointer whitespace-nowrap">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-edit-line text-sm" />
              </div>
              Write the First Review
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((review) => (
              <div key={review.id} className="bg-[#0D0D0D] rounded-3xl p-6 text-white flex flex-col">
                {/* Top: avatar + info */}
                <div className="flex items-center gap-3 mb-4">
                  <img
                    src={review.avatar}
                    alt={review.user}
                    className="w-12 h-12 rounded-full object-cover shrink-0"
                  />
                  <div className="flex-1">
                    <p className="font-bold text-sm">{review.user}</p>
                    <p className="text-white/50 text-xs">{review.establishment}</p>
                  </div>
                  {review.verified && (
                    <div className="w-6 h-6 flex items-center justify-center text-green-400">
                      <i className="ri-verified-badge-fill text-base" />
                    </div>
                  )}
                </div>

                {/* Stars */}
                <div className="flex gap-0.5 mb-3">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="w-4 h-4 flex items-center justify-center">
                      <i className={`ri-star-fill text-sm ${i < review.rating ? 'text-amber-400' : 'text-white/20'}`} />
                    </div>
                  ))}
                </div>

                {/* Quote icon */}
                <div className="w-8 h-8 flex items-center justify-center text-[#FF6B35]/40 mb-2">
                  <i className="ri-double-quotes-l text-3xl" />
                </div>

                {/* Comment */}
                <p className="text-white/80 text-sm leading-relaxed flex-1 mb-4">
                  {review.comment}
                </p>

                {/* Footer */}
                <div className="flex items-center justify-between mt-auto pt-4 border-t border-white/10">
                  <span className="text-white/30 text-xs">{review.date}</span>
                  <button
                    onClick={() => handleHelpful(review.id)}
                    className={`flex items-center gap-1.5 text-xs cursor-pointer whitespace-nowrap transition-colors ${
                      helpfulClicked.includes(review.id) ? 'text-[#FF6B35]' : 'text-white/40 hover:text-white/70'
                    }`}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-thumb-up-line text-sm" />
                    </div>
                    Helpful ({helpfulClicked.includes(review.id) ? review.helpful + 1 : review.helpful})
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Write review CTA */}
        <div className="flex justify-center mt-12">
          <button className="border-2 border-[#0D0D0D] text-[#0D0D0D] font-semibold px-10 py-4 rounded-full flex items-center gap-3 hover:bg-[#0D0D0D] hover:text-white transition-colors cursor-pointer whitespace-nowrap">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-edit-line" />
            </div>
            Write a Review
          </button>
        </div>
      </div>
    </section>
  );
}
